import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { useConfirm } from 'components/ConfirmDialog';
import { useInvalidateExternalGeoEventQuery } from 'hooks/useExternalGeoEvents';
import { ExternalGeoEvent } from 'shared/types';
import { useAuthState } from 'state/useAuthState';
import { postApproval } from './ExternalGeoEventApprove.utils';

type ExternalGeoEventApproveProps = {
  event: ExternalGeoEvent;
};

export const ExternalGeoEventApprove = (
  props: ExternalGeoEventApproveProps,
): JSX.Element | null => {
  const { event } = props;
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { permissions } = useAuthState();
  const invalidateGeoEventQuery = useInvalidateExternalGeoEventQuery(event.id);
  const queryClient = useQueryClient();

  const approveMutation = useMutation({
    mutationFn: () => postApproval(event.externalSource, event.externalId),
    onSuccess: async () => {
      enqueueSnackbar(t('externalGeoEvent.approve.posted'), {
        variant: 'success',
      });
      await invalidateGeoEventQuery();
      await queryClient.invalidateQueries({ queryKey: ['geoEvents'] });
    },
    onError: () =>
      enqueueSnackbar(t('common.errorMessage'), { variant: 'error' }),
  });

  const onApprove = async (): Promise<void> => {
    const resp = await confirm(t('externalGeoEvent.approve.dialogContent'), {
      title: t('externalGeoEvent.approve.dialogTitle'),
      okText: t('externalGeoEvent.approve.dialogOk'),
    });
    if (!resp) return;

    approveMutation.mutate();
  };

  if (!event.isApprovable || !permissions.canReportWildfire) return null;

  return (
    <Box sx={{ pb: 1, px: 2 }}>
      <Button
        color="primary"
        disabled={approveMutation.isPending}
        fullWidth
        onClick={onApprove}
        size="large"
        sx={{ fontWeight: 'fontWeightBold', mt: 2 }}
      >
        {t('externalGeoEvent.approve.button')}
      </Button>
    </Box>
  );
};
